import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

/* eslint-disable import/no-extraneous-dependencies */
// Untyped `.js` icon imports resolve as `any`.
import NotificationEmailIcon from 'icons/invites';
import NotificationFlowJoIcon from 'icons/invites-flowjo';
import NotificationSeqGeqIcon from 'icons/invites-seqgeq';
import StatusInfoIcon from 'icons/status-info';
import StatusSuccessIcon from 'icons/status-success';
import StatusWarningIcon from 'icons/status-warning';

export const ICONS = {
  NOTIFICATION_EMAIL: 'notification-email',
  NOTIFICATION_FLOWJO: 'notification-flowjo',
  NOTIFICATION_SEQGEQ: 'notification-seqgeq',
  STATUS_INFO: 'status-info',
  STATUS_SUCCESS: 'status-success',
  STATUS_WARNING: 'status-warning',
} as const;

interface IconStyle {
  primaryColor: string;
  bgColor: string;
}

type IconComponent = React.ComponentType<{ style: IconStyle }>;

const getIcon = (icon: string): IconComponent | undefined =>
  (
    {
      [ICONS.NOTIFICATION_EMAIL]: NotificationEmailIcon,
      [ICONS.NOTIFICATION_FLOWJO]: NotificationFlowJoIcon,
      [ICONS.NOTIFICATION_SEQGEQ]: NotificationSeqGeqIcon,
      [ICONS.STATUS_INFO]: StatusInfoIcon,
      [ICONS.STATUS_SUCCESS]: StatusSuccessIcon,
      [ICONS.STATUS_WARNING]: StatusWarningIcon,
    } as Record<string, IconComponent>
  )[icon];

/** Local theme shape supplied to this component's ThemeProvider. */
interface NotificationTheme {
  primaryColor: string;
  bgColor: string;
  height: string;
}

export interface FjNotificationStyle {
  primaryColor?: string;
  bgColor?: string;
  height?: string;
}

export interface FjNotificationProps {
  message: React.ReactNode;
  handleClose?: React.MouseEventHandler<HTMLButtonElement>;
  icon?: string;
  style?: FjNotificationStyle;
  className?: string;
}

export const FjNotification = ({
  message,
  handleClose,
  icon = ICONS.STATUS_INFO,
  style = {},
  className,
}: FjNotificationProps): React.ReactElement => {
  const styleOrDefault: NotificationTheme = {
    primaryColor: style.primaryColor || 'black',
    bgColor: style.bgColor || 'white',
    height: style.height || '70px',
  };
  const Icon = getIcon(icon);
  let iconStyle: IconStyle;
  if (
    icon === ICONS.STATUS_INFO ||
    icon === ICONS.STATUS_SUCCESS ||
    icon === ICONS.STATUS_WARNING
  ) {
    iconStyle = {
      primaryColor: styleOrDefault.bgColor,
      bgColor: styleOrDefault.primaryColor,
    };
  } else {
    iconStyle = {
      primaryColor: styleOrDefault.primaryColor,
      bgColor: styleOrDefault.bgColor,
    };
  }
  return (
    <ThemeProvider theme={styleOrDefault as unknown as DefaultTheme}>
      <ComputedContainer className={className}>
        <IconContainer>{Icon && <Icon style={iconStyle} />}</IconContainer>
        <Message>{message}</Message>
        {handleClose && (
          <Close id="closeButton" onClick={handleClose}>
            <span>×</span>
          </Close>
        )}
      </ComputedContainer>
    </ThemeProvider>
  );
};

export default FjNotification;

// The ThemeProvider above supplies a local `NotificationTheme`, so the styled
// interpolations read theme fields that are not part of the app `DefaultTheme`.
const themeOf = (theme: DefaultTheme): NotificationTheme =>
  theme as unknown as NotificationTheme;

const Container = styled.div`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  padding: 8px;
  box-shadow: 0px 3px 12px rgba(0, 0, 0, 0.25);
  min-height: ${({ theme }) => themeOf(theme).height};
  margin-top: 10px;
`;

const ComputedContainer = styled(Container)`
  border: 0.5px solid ${({ theme }) => themeOf(theme).primaryColor};
  background-color: ${({ theme }) => themeOf(theme).bgColor};
`;

const IconContainer = styled.div`
  flex: 0 0 calc(${({ theme }) => themeOf(theme).height} - 10px);
  width: calc(${({ theme }) => themeOf(theme).height} - 10px);
  height: calc(${({ theme }) => themeOf(theme).height} - 10px);
  margin: 0 10px;
  font-size: calc(${({ theme }) => themeOf(theme).height} - 10px);
`;

const Message = styled.div<{ handleClose?: React.MouseEventHandler<HTMLButtonElement> }>`
  flex: 1 1 100%;
  font-size: 18px;
  padding: ${({ handleClose }) => `0 ${handleClose ? 10 : 20}px 0 10px`};
`;

const Close = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  width: auto;
  overflow: visible;
  background: transparent;
  -webkit-appearance: none;
  padding: 0 8px 5px 5px;
  font-size: 22px;
  color: ${({ theme }) => themeOf(theme).primaryColor};
  cursor: pointer;
`;
